/**
 * The console client of the CP machine identity admin API (plan 2026-10-21-release-on-zion-build,
 * WS-17, R-042 item 1, R-044 item 2).
 *
 * What it is for. Each job of `.zion/workflows/release.yml` federates to its own machine principal.
 * A platform administrator makes that principal, its trust policies, its site grants and its
 * resource grants in the console, and this module is the one client of those routes. The two panes
 * `MachineIdentitiesPane` (the list) and `MachineIdentityPane` (one principal) call it.
 *
 * The routes, the permission that gates each one, and the function that sends it:
 *
 * | Route | Permission | Function |
 * |---|---|---|
 * | `GET /v1/admin/machine-principals` | `cp:machine-principal:read` | {@link listMachinePrincipals}, {@link findMachinePrincipal} |
 * | `POST /v1/admin/machine-principals` | `cp:machine-principal:create` | {@link createMachinePrincipal} |
 * | `GET /v1/admin/machine-principals/:id` | `cp:machine-principal:read` | {@link getMachinePrincipal} |
 * | `POST .../:id/disable` | `cp:machine-principal:disable` | {@link disableMachinePrincipal} |
 * | `POST .../:id/trust-policies` | `cp:machine-trust-policy:create` | {@link createMachineTrustPolicy} |
 * | `POST .../:id/trust-policies/:policyId/disable` | `cp:machine-trust-policy:disable` | {@link disableMachineTrustPolicy} |
 * | `GET .../:id/site-grants` | `cp:machine-site-grant:read` | {@link listMachineSiteGrants} |
 * | `PUT .../:id/site-grants/:permission` | `cp:machine-site-grant:write` | {@link putMachineSiteGrant} |
 * | `DELETE .../:id/site-grants/:permission` | `cp:machine-site-grant:write` | {@link deleteMachineSiteGrant} |
 * | `GET /v1/admin/permissions/grants?principalType=machine&principalId=:id` | `cp:permission-grant:read` | {@link listMachineResourceGrants} |
 * | `POST /v1/admin/permissions/grants` | `cp:permission:grant` | {@link createMachineResourceGrant} |
 * | `DELETE /v1/admin/permissions/grants/:grantId` | `cp:permission:grant` | {@link revokeMachineResourceGrant} |
 *
 * The gateway sends `/v1/admin/machine-principals` to cp-auth-api (the `PathPrefix` rule of
 * `httproute.yaml`), and `/v1/admin/permissions/grants` to site-admin-api, which forwards it to
 * cp-auth-api's `/v1/permissions/grants`.
 *
 * ## The contract module owns every shape and every path
 *
 * The paths, the request schemas and the answer schemas come from `machine-identity-admin.ts` of
 * `@zion/sdk-contracts`, which cp-auth-api binds on its side (WS-15b). This module redeclares none
 * of them. It adds two rules on the console side:
 *
 * 1. Each write parses its body with the request schema before it sends. A body the server would
 *    refuse with 400 throws the `ZodError` here and sends no request (F3 of `00-index.md`).
 * 2. Each 2xx answer parses with the answer schema. An answer outside it throws
 *    {@link MachineIdentityContractError} with the first issue, and the caller shows a refusal in
 *    place of the wrong data (F3).
 * 3. Each 2xx answer must name what the caller asked for: the requested principal, policy, grant
 *    or permission. A create answer must also carry each submitted field: the slug and display
 *    name of an enabled principal, every field of an enabled trust policy, and the principal,
 *    resource and role of an active resource grant. A disable answer must carry `disabled: true`.
 *    An answer about another subject throws {@link MachineIdentityContractError} with the issue
 *    `<field>: names another <subject> than the request`, so no pane builds a control on a row of
 *    another principal and no create navigates to one (F3). A principal create also proves the
 *    `id` of its answer with one read of the list ({@link createMachinePrincipal}).
 *
 * A non-2xx answer rejects with `ApiError` from `platform/http/client`, with `status` and the
 * server's body. No function retries, caches, polls or writes to a storage.
 */
import {
  CP_MACHINE_PRINCIPALS_PATH,
  CP_MACHINE_RESOURCE_GRANTS_PATH,
  CpMachineIdentityPathError,
  cpMachineIdentityIdSchema,
  cpMachinePrincipalDetailSchema,
  cpMachinePrincipalDisablePath,
  cpMachinePrincipalListSchema,
  cpMachinePrincipalPath,
  cpMachinePrincipalSchema,
  cpMachineResourceGrantListSchema,
  cpMachineResourceGrantRowSchema,
  cpMachineSiteGrantListSchema,
  cpMachineSiteGrantPath,
  cpMachineSiteGrantSchema,
  cpMachineSiteGrantsPath,
  cpMachineTrustPoliciesPath,
  cpMachineTrustPolicyDisablePath,
  cpMachineTrustPolicySchema,
  createCpMachinePrincipalRequestSchema,
  createCpMachineResourceGrantRequestSchema,
  createCpMachineTrustPolicyRequestSchema,
  type CpMachinePrincipal,
  type CpMachinePrincipalDetail,
  type CpMachinePrincipalList,
  type CpMachineResourceGrantList,
  type CpMachineResourceGrantRow,
  type CpMachineSiteGrant,
  type CpMachineSiteGrantList,
  type CpMachineSitePermission,
  type CpMachineTrustPolicy,
  type CpPermission,
  type CreateCpMachinePrincipalRequest,
  type CreateCpMachineResourceGrantRequest,
  type CreateCpMachineTrustPolicyRequest,
} from '@zion/sdk-contracts';
import { ApiError, api } from '../platform/http/client';
import { grantPath } from './identity.api';

/** Gates the list page, the manifest entry and the principal detail read. */
export const MACHINE_PRINCIPAL_READ_PERMISSION = 'cp:machine-principal:read' satisfies CpPermission;

/** Gates New machine identity, `POST /v1/admin/machine-principals`. */
export const MACHINE_PRINCIPAL_CREATE_PERMISSION = 'cp:machine-principal:create' satisfies CpPermission;

/** Gates Disable on a principal, `POST .../:id/disable`. */
export const MACHINE_PRINCIPAL_DISABLE_PERMISSION = 'cp:machine-principal:disable' satisfies CpPermission;

/** Gates Add trust policy, `POST .../:id/trust-policies`. */
export const MACHINE_TRUST_POLICY_CREATE_PERMISSION = 'cp:machine-trust-policy:create' satisfies CpPermission;

/**
 * Gates the Trust policies card of the detail page. The detail answer of
 * `GET /v1/admin/machine-principals/:id` carries the policies under `cp:machine-principal:read`
 * alone, so the page sends that read only to a holder of this permission, the gate of
 * `GET .../:id/trust-policies`, and reads any other caller's principal from the list.
 */
export const MACHINE_TRUST_POLICY_READ_PERMISSION = 'cp:machine-trust-policy:read' satisfies CpPermission;

/** Gates Disable on a trust policy, `POST .../:id/trust-policies/:policyId/disable`. */
export const MACHINE_TRUST_POLICY_DISABLE_PERMISSION = 'cp:machine-trust-policy:disable' satisfies CpPermission;

/** Gates the site grant section, `GET .../:id/site-grants`. */
export const MACHINE_SITE_GRANT_READ_PERMISSION = 'cp:machine-site-grant:read' satisfies CpPermission;

/** Gates Add site grant and Remove, the `PUT` and `DELETE` of `.../:id/site-grants/:permission`. */
export const MACHINE_SITE_GRANT_WRITE_PERMISSION = 'cp:machine-site-grant:write' satisfies CpPermission;

/** Gates the resource grant section, the grant list read with the machine filter. */
export const MACHINE_RESOURCE_GRANT_READ_PERMISSION = 'cp:permission-grant:read' satisfies CpPermission;

/** Gates Add resource grant and Revoke, `POST` and `DELETE` on the grant route. */
export const MACHINE_RESOURCE_GRANT_WRITE_PERMISSION = 'cp:permission:grant' satisfies CpPermission;

/**
 * The second permission a `platform` resource grant needs: the grant route answers 403 to a caller
 * without it (`platform-grant-policy.ts` of cp-auth-api). The resource grant dialog offers the
 * `platform` resource type only to a holder.
 */
export const MACHINE_PLATFORM_GRANT_PERMISSION = 'cp:platform:admin' satisfies CpPermission;

/**
 * A 2xx answer of a machine identity route that its answer schema refuses.
 *
 * `read` names the read, for example `machine principal list`. `issue` is the first zod issue as
 * `<path>: <message>`, or `answer: <message>` at the root. The message is one sentence that a pane
 * shows in its `ErrorState`. It holds no field value of the answer.
 */
export class MachineIdentityContractError extends Error {
  constructor(
    readonly read: string,
    readonly issue: string,
  ) {
    super(`The control plane answered the ${read} in a shape this console does not accept (${issue}).`);
    this.name = 'MachineIdentityContractError';
  }
}

/* The first issue of a failed parse as `path: message`, or `answer: message` at the root. */
function firstIssue(issues: readonly AnswerIssue[]): string {
  return issues
    .slice(0, 1)
    .map((issue) => `${issue.path.length === 0 ? 'answer' : issue.path.map(String).join('.')}: ${issue.message}`)
    .join('');
}

/**
 * Thrown by {@link revokeMachineResourceGrant} for a grant id that is not a lower-case UUID. Every
 * grant id the server answers is one, so only a caller defect reaches it.
 */
export class MachineResourceGrantIdError extends Error {
  constructor() {
    super('machine resource grant revoke: grantId is not a lower-case UUID');
    this.name = 'MachineResourceGrantIdError';
  }
}

/**
 * Thrown by {@link createMachineTrustPolicy} for a claim name that the contract request schema drops
 * from its parsed body in place of a refusal: `__proto__`, which zod 4 leaves out of a parsed record.
 * The body would then reach the server without that condition, so the call sends nothing. `claim`
 * is the dropped name. Only a caller that skips the dialog guard (`claimsProblem`) reaches it.
 */
export class MachineTrustPolicyClaimError extends Error {
  constructor(readonly claim: string) {
    super(`machine trust policy create: the claim name ${claim} cannot be sent`);
    this.name = 'MachineTrustPolicyClaimError';
  }
}

/* One zod issue, as far as this module reads it. */
interface AnswerIssue {
  readonly path: readonly PropertyKey[];
  readonly message: string;
}

/* The `safeParse` of an answer schema, as far as this module reads it. */
interface AnswerSchema<T> {
  safeParse(value: unknown): { readonly success: true; readonly data: T } | { readonly success: false; readonly error: { readonly issues: readonly AnswerIssue[] } };
}

/* Parse one answer, or throw the contract error that names the read and the first issue. */
function parseAnswer<T>(schema: AnswerSchema<T>, value: unknown, read: string): T {
  const parsed = schema.safeParse(value);
  if (!parsed.success) throw new MachineIdentityContractError(read, firstIssue(parsed.error.issues));
  return parsed.data;
}

/*
 * Rule 3 of the header: the first field of `checks` whose answer value differs from the requested
 * value throws the contract error `<field>: names another <subject> than the request`. The issue
 * carries no value of the answer.
 */
function requireBound<T>(answer: T, read: string, subject: string, checks: readonly (readonly [string, unknown, unknown])[]): T {
  const wrong = checks.find(([, got, wanted]) => got !== wanted);
  if (wrong !== undefined) throw new MachineIdentityContractError(read, `${wrong[0]}: names another ${subject} than the request`);
  return answer;
}

/**
 * `true` when a rejected write may have changed the control plane, so the console cannot tell
 * whether it failed. A dialog then holds its submit and the page reads again (the house rule of
 * `InFlightPane`). Pure.
 *
 * Output per input class:
 * - `false` for a refusal raised before any send: a request `ZodError` (matched by its `name`,
 *   because the package does not depend on `zod`), {@link CpMachineIdentityPathError},
 *   {@link MachineResourceGrantIdError} and {@link MachineTrustPolicyClaimError};
 * - `false` for an `ApiError` of status 400 to 499: the control plane refused the request before
 *   it changed anything;
 * - `true` for an `ApiError` of status 500 to 599. A server error does not prove that the write
 *   rolled back: the site-admin-api grant proxy answers 503 when it loses the upstream answer of a
 *   write that cp-auth-api committed, and a gateway answers 502 or 504 in the same case;
 * - `true` for an `ApiError` with a status below 400 (a 2xx body that is not JSON, or a 3xx), for
 *   {@link MachineIdentityContractError} (a 2xx answer outside the contract), for
 *   {@link MachineWriteTimeoutError} (a write with no answer in time), and for every other value
 *   (a network fault).
 */
export function isUncertainWriteFailure(cause: unknown): boolean {
  if (cause instanceof CpMachineIdentityPathError || cause instanceof MachineResourceGrantIdError || cause instanceof MachineTrustPolicyClaimError) {
    return false;
  }
  if (cause instanceof Error && cause.name === 'ZodError') return false;
  if (!(cause instanceof ApiError)) return true;
  return cause.status < 400 || cause.status >= 500;
}

/**
 * The longest time a dialog waits for the answer of one machine identity write, in milliseconds.
 * The gateway's route timeout is 30 s, so a write still open at 45 s has no answer to wait for. The
 * same bound as `REMEDY_TIMEOUT_MS` of `provisioningJobs.api.ts`.
 */
export const MACHINE_WRITE_TIMEOUT_MS = 45_000;

/**
 * The rejection of a write that had no answer in {@link MACHINE_WRITE_TIMEOUT_MS}. The control plane
 * may have made the change, so {@link isUncertainWriteFailure} answers `true` for it. The message
 * holds no value of the request.
 */
export class MachineWriteTimeoutError extends Error {
  constructor() {
    super(`The write did not finish in ${MACHINE_WRITE_TIMEOUT_MS / 1000} seconds.`);
    this.name = 'MachineWriteTimeoutError';
  }
}

/**
 * Run one write with the time bound {@link MACHINE_WRITE_TIMEOUT_MS}, as `postRemedy` of
 * `provisioningJobs.api.ts` bounds a remedy. Output: the answer of `write` when it settles first.
 * Errors: the rejection of `write` when it rejects first, and {@link MachineWriteTimeoutError} when
 * the bound passes first. The clock starts before `write` runs. The request keeps running after the
 * bound, and its late answer goes nowhere. Side effects: one `AbortSignal.timeout` and its abort
 * listener, which the call removes when it settles.
 */
export async function withMachineWriteBound<T>(write: () => Promise<T>): Promise<T> {
  const signal = AbortSignal.timeout(MACHINE_WRITE_TIMEOUT_MS);
  const settled = new AbortController();
  const bound = new Promise<never>((_, reject) => {
    signal.addEventListener('abort', () => reject(new MachineWriteTimeoutError()), { once: true, signal: settled.signal });
  });
  try {
    return await Promise.race([write(), bound]);
  } finally {
    settled.abort();
  }
}

/**
 * Read every machine principal, in the server's order (ascending slug in cp-auth-api).
 *
 * Output: the parsed list. Errors: `ApiError` 403 without `cp:machine-principal:read`;
 * {@link MachineIdentityContractError} for an answer outside `cpMachinePrincipalListSchema`.
 * Side effects: one GET.
 */
export async function listMachinePrincipals(): Promise<CpMachinePrincipalList> {
  return parseAnswer(cpMachinePrincipalListSchema, await api.get<unknown>(CP_MACHINE_PRINCIPALS_PATH), 'machine principal list');
}

/**
 * Make one machine principal of kind `external`, with no trust policy and no grant.
 *
 * Input: `slug` (3 to 64 characters of `^[a-z0-9][a-z0-9-]*[a-z0-9]$`) and `displayName` (1 to 200
 * characters). Output: the new principal, from the 201 answer.
 *
 * The 201 answer names the principal the list page then opens, so the call proves its `id`: after
 * the bind of rule 3 it reads the principal list once and requires the row of that `id` to carry
 * the submitted `slug` and `displayName`. A slug is unique on the control plane, so an answer that
 * puts the id of an existing principal beside the submitted fields fails the proof, and no page
 * opens on that principal. The caller needs `cp:machine-principal:read` for the proof read, as the
 * list page does.
 *
 * Errors: a body outside `createCpMachinePrincipalRequestSchema` throws its `ZodError` and sends
 * nothing; `ApiError` 409 for a slug that exists, 400 for a door-pull slug;
 * {@link MachineIdentityContractError} for an answer outside `cpMachinePrincipalSchema`, for an
 * answer whose `slug` or `displayName` is not the submitted value or whose `disabled` is `true`
 * (cp-auth-api stores both fields as sent and makes every principal enabled, so such an answer names
 * another principal), and with the issue `id: the principal list does not confirm the new
 * principal` when the proof read fails or its row of that `id` is absent or carries another slug or
 * display name. Every such error is an uncertain write ({@link isUncertainWriteFailure}): the POST
 * may have made a principal. Side effects: one POST, which writes one audit row, then one GET of the
 * list.
 */
export async function createMachinePrincipal(body: CreateCpMachinePrincipalRequest): Promise<CpMachinePrincipal> {
  const checked = createCpMachinePrincipalRequestSchema.parse(body);
  const read = 'new machine principal';
  const principal = parseAnswer(cpMachinePrincipalSchema, await api.post<unknown>(CP_MACHINE_PRINCIPALS_PATH, checked), read);
  requireBound(principal, read, 'machine principal', [
    ['slug', principal.slug, checked.slug],
    ['displayName', principal.displayName, checked.displayName],
    ['disabled', principal.disabled, false],
  ]);
  const listed = await listMachinePrincipals().then(
    (rows) => rows.find((row) => row.id === principal.id),
    () => undefined,
  );
  if (listed === undefined || listed.slug !== checked.slug || listed.displayName !== checked.displayName) {
    throw new MachineIdentityContractError(read, 'id: the principal list does not confirm the new principal');
  }
  return principal;
}

/**
 * Read one principal with every trust policy, disabled policies included. cp-auth-api answers the
 * policies under `cp:machine-principal:read` alone, so the detail page sends this read only for a
 * holder of {@link MACHINE_TRUST_POLICY_READ_PERMISSION}, and {@link findMachinePrincipal} for any
 * other caller.
 *
 * Input: `principalId`, a lower-case UUID; any other string throws `CpMachineIdentityPathError` and
 * sends nothing. Output: the parsed detail. Errors: `ApiError` 404 `machine principal <id> not
 * found`; {@link MachineIdentityContractError} for an answer outside the schema, for an answer
 * whose `id` is not `principalId`, and for a policy whose `machinePrincipalId` is not `principalId`.
 * Side effects: one GET.
 */
export async function getMachinePrincipal(principalId: string): Promise<CpMachinePrincipalDetail> {
  const detail = parseAnswer(cpMachinePrincipalDetailSchema, await api.get<unknown>(cpMachinePrincipalPath(principalId)), 'machine principal');
  return requireBound(detail, 'machine principal', 'machine principal', [
    ['id', detail.id, principalId],
    ...detail.trustPolicies.map((policy, index) => [`trustPolicies.${index}.machinePrincipalId`, policy.machinePrincipalId, principalId] as const),
  ]);
}

/**
 * Read one principal with no trust policy, from the principal list. The detail page calls it for a
 * caller without {@link MACHINE_TRUST_POLICY_READ_PERMISSION}: the detail route answers every
 * policy under `cp:machine-principal:read` alone, so the page must not send that read to a caller
 * who may not see the policies. The list route answers no policy.
 *
 * Input: `principalId`, a lower-case UUID; any other string throws `CpMachineIdentityPathError`
 * with `argument` `principalId` and sends nothing. Output: the list row whose `id` is
 * `principalId`. Errors: `ApiError` 404 with the body `{ statusCode: 404, message: 'machine
 * principal <id> not found', error: 'Not Found' }` when the list holds no such row, the answer of
 * the detail route for an unknown id; the errors of {@link listMachinePrincipals}. Side effects: one
 * GET of the list.
 */
export async function findMachinePrincipal(principalId: string): Promise<CpMachinePrincipal> {
  if (!cpMachineIdentityIdSchema.safeParse(principalId).success) throw new CpMachineIdentityPathError('principalId');
  const found = (await listMachinePrincipals()).find((principal) => principal.id === principalId);
  if (found !== undefined) return found;
  throw new ApiError(404, { statusCode: 404, message: `machine principal ${principalId} not found`, error: 'Not Found' });
}

/**
 * Disable one principal. The route is idempotent: a disabled principal answers unchanged. A
 * disabled principal mints no new token; a token that it minted before keeps working until `exp`.
 *
 * Input and path errors: as {@link getMachinePrincipal}. Output: the principal from the 200 answer.
 * Errors: `ApiError` 404 for an unknown principal, 409 for a door-pull principal;
 * {@link MachineIdentityContractError}, also for an answer whose `id` is not `principalId`, and for
 * an answer whose `disabled` is `false`: cp-auth-api answers a disabled row on every success, so an
 * enabled row says that the disable did not hold, and no dialog may report it as done. Side
 * effects: one POST, one audit row on a change.
 */
export async function disableMachinePrincipal(principalId: string): Promise<CpMachinePrincipal> {
  const read = 'disabled machine principal';
  const principal = parseAnswer(cpMachinePrincipalSchema, await api.post<unknown>(cpMachinePrincipalDisablePath(principalId)), read);
  return requireBound(principal, read, 'machine principal', [
    ['id', principal.id, principalId],
    ['disabled', principal.disabled, true],
  ]);
}

/**
 * Add one trust policy to a principal.
 *
 * Input: the principal id and a body of `createCpMachineTrustPolicyRequestSchema`. A body outside it
 * throws its `ZodError` and sends nothing. A claim name that the schema drops from its parsed body
 * (`__proto__`) throws {@link MachineTrustPolicyClaimError} and sends nothing. Output: the new
 * policy from the 201 answer. Errors:
 * - `ApiError` 400 for the pipe rules of cp-auth-api past the contract: a `requiredRefPrefix`
 *   outside `refs/heads/release/`, a prefix with `..` or a `.lock` segment, claims longer than 1024
 *   code points in the database text form, a claim with U+0000 or an unpaired surrogate;
 * - `ApiError` 400 for the service rules: a registered-key issuer of another principal, a policy of
 *   the release principal outside the release shape, a scoped policy on the plane's own host, an
 *   unscoped `release-source:` subject (R-034);
 * - `ApiError` 404 for an unknown principal;
 * - `ApiError` 409 for a door-pull principal, an identical, subsumed or overlapping enabled policy,
 *   a match key that a disabled policy holds, and, on a principal that holds a site grant, a policy
 *   on the plane's own host, a scoped policy or a policy with no `requiredRefPrefix`;
 * - {@link MachineIdentityContractError}, also for an answer whose `machinePrincipalId` is not
 *   `principalId`, and for an answer whose `issuer`, `subject`, `requiredClaims`,
 *   `releaseSourceScoped`, `requiredRefPrefix` or `tokenTtlSeconds` is not the submitted value or
 *   whose `disabled` is `true`. cp-auth-api stores each field as sent (an absent
 *   `releaseSourceScoped` as `false`, an absent `requiredRefPrefix` or `tokenTtlSeconds` as `null`,
 *   plan 2026-10-31-door-long-push) and makes every policy enabled, so such an answer names another
 *   trust policy, and the dialog must not report it as the one the administrator made.
 *
 * Side effects: one POST, one audit row.
 */
export async function createMachineTrustPolicy(
  principalId: string,
  body: CreateCpMachineTrustPolicyRequest,
): Promise<CpMachineTrustPolicy> {
  const path = cpMachineTrustPoliciesPath(principalId);
  const checked = createCpMachineTrustPolicyRequestSchema.parse(body);
  const dropped = Object.keys(body.requiredClaims).find((claim) => !Object.hasOwn(checked.requiredClaims, claim));
  if (dropped !== undefined) throw new MachineTrustPolicyClaimError(dropped);
  const read = 'new trust policy';
  const policy = parseAnswer(cpMachineTrustPolicySchema, await api.post<unknown>(path, checked), read);
  requireBound(policy, read, 'machine principal', [['machinePrincipalId', policy.machinePrincipalId, principalId]]);
  return requireBound(policy, read, 'trust policy', [
    ['issuer', policy.issuer, checked.issuer],
    ['subject', policy.subject, checked.subject],
    ['requiredClaims', claimSetKey(policy.requiredClaims), claimSetKey(checked.requiredClaims)],
    ['releaseSourceScoped', policy.releaseSourceScoped, checked.releaseSourceScoped ?? false],
    ['requiredRefPrefix', policy.requiredRefPrefix, checked.requiredRefPrefix ?? null],
    ['tokenTtlSeconds', policy.tokenTtlSeconds, checked.tokenTtlSeconds ?? null],
    ['disabled', policy.disabled, false],
  ]);
}

/*
 * One string per claim set, equal for two sets with the same keys and values in any key order:
 * the JSON text of the entries sorted by key. `jsonb` does not keep the key order of the body, so
 * the answer of a create may list the claims in another order. Pure.
 */
function claimSetKey(claims: Readonly<Record<string, string>>): string {
  return JSON.stringify(Object.entries(claims).sort(([a], [b]) => (a < b ? -1 : 1)));
}

/**
 * Disable one trust policy. The route is idempotent. A disabled policy matches no new exchange.
 *
 * Input: two lower-case UUIDs; a refused argument throws `CpMachineIdentityPathError` and sends
 * nothing. Output: the policy from the 200 answer. Errors: `ApiError` 404 `trust policy <policyId>
 * not found for machine principal <id>` or `machine principal <id> not found`, 409 for a door-pull
 * principal; {@link MachineIdentityContractError}, also for an answer whose `id` is not `policyId` or
 * whose `machinePrincipalId` is not `principalId`, and for an answer whose `disabled` is `false`:
 * cp-auth-api answers a disabled row on every success, so an enabled row says that the policy still
 * matches. Side effects: one POST, one audit row on a change.
 */
export async function disableMachineTrustPolicy(principalId: string, policyId: string): Promise<CpMachineTrustPolicy> {
  const read = 'disabled trust policy';
  const policy = parseAnswer(cpMachineTrustPolicySchema, await api.post<unknown>(cpMachineTrustPolicyDisablePath(principalId, policyId)), read);
  return requireBound(policy, read, 'trust policy', [
    ['id', policy.id, policyId],
    ['machinePrincipalId', policy.machinePrincipalId, principalId],
    ['disabled', policy.disabled, true],
  ]);
}

/**
 * The read key and path of the site grant list of one principal: `cpMachineSiteGrantsPath` of the
 * contract. Panes take their read keys from this module, as they take every path from an api module.
 *
 * Input and errors: a lower-case UUID; any other string throws `CpMachineIdentityPathError`. No side
 * effect.
 */
export function machineSiteGrantsPath(principalId: string): string {
  return cpMachineSiteGrantsPath(principalId);
}

/**
 * Read the site grants of one principal: at most one row per member of
 * `CP_MACHINE_SITE_PERMISSIONS`, in ascending order of `permission`.
 *
 * Errors: `ApiError` 403 without `cp:machine-site-grant:read`, 404 for an unknown principal;
 * {@link MachineIdentityContractError} for an answer outside `cpMachineSiteGrantListSchema`, which
 * also refuses two rows of one permission and rows out of order, and for a row whose
 * `machinePrincipalId` is not `principalId`. Side effects: one GET.
 */
export async function listMachineSiteGrants(principalId: string): Promise<CpMachineSiteGrantList> {
  const read = 'site grant list';
  const grants = parseAnswer(cpMachineSiteGrantListSchema, await api.get<unknown>(machineSiteGrantsPath(principalId)), read);
  return requireBound(
    grants,
    read,
    'machine principal',
    grants.map((grant, index) => [`${index}.machinePrincipalId`, grant.machinePrincipalId, principalId] as const),
  );
}

/**
 * Give a principal one site permission. The route is idempotent: a row that exists answers with its
 * first `createdAt`. The permission then enters every token the principal mints.
 *
 * Input: a lower-case UUID and a member of `CP_MACHINE_SITE_PERMISSIONS`; any other argument throws
 * `CpMachineIdentityPathError` and sends nothing. Output: the row from the 200 answer. Errors:
 * `ApiError` 404 for an unknown principal; 409 for a door-pull or a disabled principal, for an
 * enabled policy on the plane's own host, and for an enabled policy whose token carries no site
 * permission (a scoped policy, no `requiredRefPrefix`, or a prefix outside `refs/heads/release/`);
 * 400 when the release principal takes a permission outside its four, the members of
 * `RELEASE_PRINCIPAL_SITE_PERMISSIONS` of cp-auth-api (any other principal may take each of the
 * seven members of `CP_MACHINE_SITE_PERMISSIONS`); {@link MachineIdentityContractError},
 * also for an answer of another principal or another permission. Side effects: one PUT with no body.
 */
export async function putMachineSiteGrant(principalId: string, permission: CpMachineSitePermission): Promise<CpMachineSiteGrant> {
  const grant = parseAnswer(cpMachineSiteGrantSchema, await api.put<unknown>(cpMachineSiteGrantPath(principalId, permission)), 'site grant');
  return requireBound(grant, 'site grant', 'site grant', [
    ['machinePrincipalId', grant.machinePrincipalId, principalId],
    ['permission', grant.permission, permission],
  ]);
}

/**
 * Take one site permission from a principal. The route is idempotent and answers 204 with no body,
 * also for a row that does not exist. A token that the principal minted before keeps the permission
 * until `exp` (at most 900 s).
 *
 * Input and path errors: as {@link putMachineSiteGrant}. Errors: `ApiError` 404 for an unknown
 * principal; `ApiError` with the answer status for a 2xx answer with a body that is not JSON;
 * {@link MachineIdentityContractError} with the issue `answer: a 204 has no body` for a 2xx answer
 * with a JSON body, because only a 204 says that the row is gone. Side effects: one DELETE.
 */
export async function deleteMachineSiteGrant(principalId: string, permission: CpMachineSitePermission): Promise<void> {
  const answer = await api.delete<unknown>(cpMachineSiteGrantPath(principalId, permission));
  if (answer !== undefined) throw new MachineIdentityContractError('site grant removal', 'answer: a 204 has no body');
}

/**
 * The grant list path of one machine principal: `CP_MACHINE_RESOURCE_GRANTS_PATH` with the query
 * `principalType=machine&principalId=<id>`.
 *
 * Input: `principalId`, a lower-case UUID. Errors: any other string throws
 * `CpMachineIdentityPathError` with `argument` `principalId`, as the contract's path builders do, so
 * no caller can put another filter into the query. No side effect.
 */
export function machineResourceGrantsPath(principalId: string): string {
  if (!cpMachineIdentityIdSchema.safeParse(principalId).success) throw new CpMachineIdentityPathError('principalId');
  const query = new URLSearchParams({ principalType: 'machine', principalId });
  return `${CP_MACHINE_RESOURCE_GRANTS_PATH}?${query.toString()}`;
}

/**
 * Read the active resource grants of one machine principal. cp-auth-api's grant list answers rows
 * with no `revokedAt` alone, so a revoked grant leaves the answer.
 *
 * Errors: `ApiError` 403 without `cp:permission-grant:read`; {@link MachineIdentityContractError}
 * for an answer outside `cpMachineResourceGrantListSchema`, which also refuses a row of another
 * principal type, and for a row whose `principalId` is not `principalId`. So no Revoke ever shows
 * on a grant of another principal. Side effects: one GET.
 */
export async function listMachineResourceGrants(principalId: string): Promise<CpMachineResourceGrantList> {
  const read = 'resource grant list';
  const rows = parseAnswer(cpMachineResourceGrantListSchema, await api.get<unknown>(machineResourceGrantsPath(principalId)), read);
  return requireBound(
    rows,
    read,
    'machine principal',
    rows.map((row, index) => [`${index}.principalId`, row.principalId, principalId] as const),
  );
}

/**
 * Give a machine principal one role on one resource.
 *
 * Input: a body of `createCpMachineResourceGrantRequestSchema` (`principalType: 'machine'`). A body
 * outside it throws its `ZodError` and sends nothing. Output: the row from the 201 answer. Errors:
 * - `ApiError` 403 for a `platform` grant without `cp:platform:admin`, and for any other grant
 *   whose role holds a permission the caller holds neither site-wide nor on the resource;
 * - `ApiError` 404 for a role that does not exist;
 * - `ApiError` 400 for a wildcard role, a seeded site role on one resource, a `platform` grant on
 *   another id, a `platform` role with a permission outside `cp:platform:publish`,
 *   `cp:platform:registry-read` and `cp:app:registry-read`, and an app image read role;
 * - `ApiError` 409 for an active duplicate or a door-pull principal;
 * - {@link MachineIdentityContractError}, also for an answer of another principal, resource or role,
 *   and for an answer whose `revokedAt` is not `null`: cp-auth-api answers an active row on every
 *   success, a new row after a revoke included, so a revoked row says that the grant does not hold.
 *
 * Side effects: one POST.
 */
export async function createMachineResourceGrant(body: CreateCpMachineResourceGrantRequest): Promise<CpMachineResourceGrantRow> {
  const checked = createCpMachineResourceGrantRequestSchema.parse(body);
  const read = 'new resource grant';
  const row = parseAnswer(cpMachineResourceGrantRowSchema, await api.post<unknown>(CP_MACHINE_RESOURCE_GRANTS_PATH, checked), read);
  return requireBound(row, read, 'resource grant', [
    ['principalId', row.principalId, checked.principalId],
    ['resourceType', row.resourceType, checked.resourceType],
    ['resourceId', row.resourceId, checked.resourceId],
    ['roleSlug', row.roleSlug, checked.roleSlug],
    ['revokedAt', row.revokedAt, null],
  ]);
}

/**
 * Revoke one resource grant of a machine principal. The route stamps `revokedAt`, the row stays,
 * and it answers 204 with no body. The path is `grantPath` of `identity.api.ts` (F2), the path that
 * `revokeGrant` sends; this function reads the answer that `revokeGrant` drops.
 *
 * Input: `grantId`, a lower-case UUID. Output: nothing, after a 204.
 *
 * Errors:
 * - any other string throws {@link MachineResourceGrantIdError} and sends nothing;
 * - `ApiError` 404 for an unknown grant, 403 for a `platform` grant without `cp:platform:admin`,
 *   409 for a door-pull principal, a grant already revoked, and the last active `app_owner` of an
 *   app;
 * - `ApiError` with the answer status for a 2xx answer with a body that is not JSON;
 * - {@link MachineIdentityContractError} with the issue `answer: a 204 has no body` for a 2xx
 *   answer with a JSON body, `null` included. Rule 3 of the header: only a 204 says that the grant
 *   is revoked. A proxy or a gateway can answer 200 with the active row, and no dialog may report
 *   that as a revoke. {@link deleteMachineSiteGrant} holds the same rule.
 *
 * Each 2xx error is an uncertain write ({@link isUncertainWriteFailure}). Side effects: one DELETE.
 */
export async function revokeMachineResourceGrant(grantId: string): Promise<void> {
  if (!cpMachineIdentityIdSchema.safeParse(grantId).success) throw new MachineResourceGrantIdError();
  const answer = await api.delete<unknown>(grantPath(grantId));
  if (answer !== undefined) throw new MachineIdentityContractError('resource grant revoke', 'answer: a 204 has no body');
}
